import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  AlertTriangle, ArrowLeft, Building2, ClipboardList, Clock, Loader2, LogOut, MapPin, RefreshCw, SearchX,
} from "lucide-react";
import DcPage from "@/components/landing/dcv2/DcPage";
import { useAuth } from "@/app/hooks/useAuth";
import { glassCardStyle, surfaceStyle } from "@/components/auth/authStyles";
import { candidateApi } from "@/services/endpoints/candidate";
import { candidateChecklistApi } from "@/services/endpoints/candidateChecklist";
import { workplaceLabel, employmentLabel } from "@/components/jobs/jobCardUtils";
import { ChecklistItemCard } from "@/features/activation-checklist/ChecklistItemCard";
import {
  apiErrorMessage,
  apiErrorStatus,
  findApplicationsForJob,
  hasAnswer,
  isChecklistOpen,
  isItemEditable,
  sortChecklistItems,
} from "@/features/activation-checklist/checklistValues";
import type {
  CandidateApplicationItem,
  CandidateChecklistItemUpdate,
  CandidateChecklistPortal,
} from "@/services/api/types";

const EASE = [0.16, 1, 0.3, 1] as const;

/** GET /candidates/me/applications allows at most 50 per page. */
const LOOKUP_PAGE_SIZE = 50;
/** Stop after 500 applications rather than page without bound. */
const LOOKUP_MAX_PAGES = 10;

type View =
  | { kind: "loading" }
  | { kind: "no-application" }
  | { kind: "pending"; application: CandidateApplicationItem }
  | { kind: "error"; message: string }
  | { kind: "ready"; portal: CandidateChecklistPortal };

/**
 * Finds the signed-in candidate's application to one job posting. The API
 * has no filter by posting, so this pages the candidate's own list and stops
 * at the first pipeline row (the one a checklist belongs to).
 */
async function lookUpApplication(jobPostingId: string) {
  let pending: CandidateApplicationItem | null = null;
  for (let page = 1; page <= LOOKUP_MAX_PAGES; page++) {
    const { items, pagination } = await candidateApi.getApplications(page, LOOKUP_PAGE_SIZE);
    const found = findApplicationsForJob(items, jobPostingId);
    if (found.hiring) return { hiring: found.hiring, pending: null };
    pending = pending ?? found.pending;
    if (page >= (pagination?.totalPages ?? 0)) break;
  }
  return { hiring: null, pending };
}

function MessageCard({
  icon: Icon,
  title,
  children,
  actions,
}: {
  icon: React.ElementType;
  title: string;
  children: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="rounded-2xl p-6 text-center sm:p-10" style={glassCardStyle}>
      <div
        className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl"
        style={{ background: "var(--landing-surface)", border: "1px solid var(--landing-glass-border)" }}
      >
        <Icon className="h-7 w-7 text-[var(--acc2)]/70" />
      </div>
      <h1 className="mb-2 text-xl font-bold tracking-tight" style={{ color: "var(--landing-text)" }}>
        {title}
      </h1>
      <div className="mx-auto max-w-md text-sm leading-relaxed [overflow-wrap:anywhere]" style={{ color: "var(--landing-text-muted)" }}>
        {children}
      </div>
      {actions && <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">{actions}</div>}
    </div>
  );
}

const secondaryButton =
  "inline-flex w-full items-center justify-center gap-2 rounded-lg px-5 py-3 text-sm font-semibold transition-colors sm:w-auto";

/**
 * `/profile/applications/:jobPostingId` — the link in the activation checklist
 * email (T102126038). The email names the job posting, so the page resolves the
 * candidate's own pipeline row for that posting, then shows its activation
 * checklist and lets the candidate answer it.
 */
export default function ProfileApplicationChecklist() {
  const { jobPostingId = "" } = useParams<{ jobPostingId: string }>();
  const { user, isAuthenticated, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();

  const [view, setView] = useState<View>({ kind: "loading" });
  const [reloadKey, setReloadKey] = useState(0);

  const returnPath = `/profile/applications/${encodeURIComponent(jobPostingId)}`;
  const signInPath = `/auth?redirect=${encodeURIComponent(returnPath)}`;

  // Wait for the stored session to load before deciding the visitor is signed
  // out: on a fresh page load (the emailed link) the first render has no user yet.
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate(signInPath, { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate, signInPath]);

  useEffect(() => {
    if (authLoading || !isAuthenticated || !user?.id) return;
    let active = true;
    setView({ kind: "loading" });

    (async () => {
      try {
        const { hiring, pending } = await lookUpApplication(jobPostingId);
        if (!active) return;
        if (!hiring) {
          setView(pending ? { kind: "pending", application: pending } : { kind: "no-application" });
          return;
        }
        const portal = await candidateChecklistApi.getPortal(hiring.id);
        if (active) setView({ kind: "ready", portal });
      } catch (e) {
        if (!active) return;
        if (apiErrorStatus(e) === 404) {
          setView({ kind: "no-application" });
          return;
        }
        setView({ kind: "error", message: apiErrorMessage(e, "We couldn't load your checklist. Please try again.") });
      }
    })();

    return () => {
      active = false;
    };
  }, [authLoading, isAuthenticated, user?.id, jobPostingId, reloadKey]);

  const items = useMemo(
    () => (view.kind === "ready" ? sortChecklistItems(view.portal.checklistItems) : []),
    [view],
  );

  const handleUpdated = (update: CandidateChecklistItemUpdate) => {
    setView((prev) => {
      if (prev.kind !== "ready") return prev;
      return {
        ...prev,
        portal: {
          ...prev.portal,
          // Only the answer fields: the update's templateItem lacks HR's per-candidate title.
          checklistItems: prev.portal.checklistItems.map((i) =>
            i.id === update.id
              ? {
                  ...i,
                  value: update.value,
                  file: update.file ?? null,
                  status: update.status ?? i.status,
                  remarks: update.remarks !== undefined ? update.remarks : i.remarks,
                  // The save stamps it (ems-api #3667); it marks a one-option dropdown answered.
                  respondedAt: update.respondedAt !== undefined ? update.respondedAt : i.respondedAt,
                }
              : i,
          ),
        },
      };
    });
  };

  const switchAccount = () => {
    logout();
    navigate(signInPath);
  };

  if (authLoading || !isAuthenticated) return null;

  const backLink = (
    <Link
      to="/profile"
      className="mb-5 inline-flex items-center gap-2 text-sm font-medium transition-colors hover:text-[var(--acc2)]"
      style={{ color: "var(--landing-text-muted)" }}
    >
      <ArrowLeft className="h-4 w-4" />
      My profile
    </Link>
  );

  let content: ReactNode;
  if (view.kind === "loading") {
    content = (
      <div className="flex flex-col items-center gap-3 rounded-2xl p-12 text-center" style={glassCardStyle}>
        <Loader2 className="h-6 w-6 animate-spin text-[var(--acc2)]" />
        <p className="text-sm" style={{ color: "var(--landing-text-muted)" }}>Loading your checklist…</p>
      </div>
    );
  } else if (view.kind === "no-application") {
    content = (
      <MessageCard
        icon={SearchX}
        title="No application found for this job"
        actions={
          <>
            <button type="button" onClick={switchAccount} className={`btn-electric ${secondaryButton}`}>
              <LogOut className="h-4 w-4" />
              Sign in with another account
            </button>
            <Link to="/profile" className={secondaryButton} style={surfaceStyle}>
              View my applications
            </Link>
          </>
        }
      >
        We couldn't find an application to this job for{" "}
        <span className="font-semibold" style={{ color: "var(--landing-text)" }}>{user?.email}</span>. If you applied
        with a different email address, sign in with that account to open your activation checklist.
      </MessageCard>
    );
  } else if (view.kind === "pending") {
    content = (
      <MessageCard
        icon={Clock}
        title="Your application is still under review"
        actions={
          <Link to="/profile" className={`btn-electric ${secondaryButton}`}>
            View my applications
          </Link>
        }
      >
        Your application for{" "}
        <span className="font-semibold" style={{ color: "var(--landing-text)" }}>{view.application.jobTitle}</span>
        {view.application.organizationName ? ` at ${view.application.organizationName}` : ""} has not reached
        activation yet. The checklist opens here once the employer moves you to that stage.
      </MessageCard>
    );
  } else if (view.kind === "error") {
    content = (
      <MessageCard
        icon={AlertTriangle}
        title="We couldn't load your checklist"
        actions={
          <button type="button" onClick={() => setReloadKey((k) => k + 1)} className={`btn-electric ${secondaryButton}`}>
            <RefreshCw className="h-4 w-4" />
            Try again
          </button>
        }
      >
        {view.message}
      </MessageCard>
    );
  } else {
    const { portal } = view;
    const open = isChecklistOpen(portal.activation);
    const total = items.length;
    const answered = items.filter(hasAnswer).length;
    const approved = items.filter((i) => String(i.status).toLowerCase() === "approved").length;
    const answeredPct = total > 0 ? Math.round((answered / total) * 100) : 0;
    const meta = [
      portal.job.location ? { icon: MapPin, text: portal.job.location } : null,
      portal.job.workplaceType ? { icon: Building2, text: workplaceLabel(portal.job.workplaceType) } : null,
      portal.job.employmentType ? { icon: Clock, text: employmentLabel(portal.job.employmentType) } : null,
    ].filter(Boolean) as { icon: React.ElementType; text: string }[];

    content = (
      <div className="space-y-5">
        <div className="rounded-2xl p-5 sm:p-6" style={glassCardStyle}>
          <p className="mb-2 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[var(--acc2)]">
            <ClipboardList className="h-3.5 w-3.5" />
            Activation checklist
          </p>
          <h1
            className="text-2xl font-bold leading-tight tracking-tight [overflow-wrap:anywhere] md:text-3xl"
            style={{ color: "var(--landing-text)" }}
          >
            {portal.job.title}
          </h1>
          <p className="mt-1 text-sm font-medium [overflow-wrap:anywhere]" style={{ color: "var(--landing-text-muted)" }}>
            {portal.organization.name}
          </p>
          {meta.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs" style={{ color: "var(--landing-text-muted)" }}>
              {meta.map(({ icon: Icon, text }, i) => (
                <span key={i} className="flex items-center gap-1">
                  <Icon className="h-3 w-3" />
                  {text}
                </span>
              ))}
            </div>
          )}

          {total > 0 && (
            <div className="mt-5">
              <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2 text-xs font-semibold">
                <span style={{ color: "var(--landing-text)" }}>
                  {answered} of {total} answered
                </span>
                <span style={{ color: "var(--landing-text-muted)" }}>{approved} approved</span>
              </div>
              <div
                className="h-2 overflow-hidden rounded-full"
                style={{ background: "var(--landing-surface)", border: "1px solid var(--landing-glass-border)" }}
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={answeredPct}
                aria-label="Checklist items answered"
              >
                <div className="h-full rounded-full transition-all" style={{ width: `${answeredPct}%`, background: "var(--acc2)" }} />
              </div>
            </div>
          )}
        </div>

        {!open && (
          <div
            className="flex items-start gap-3 rounded-2xl px-4 py-3.5 text-sm"
            style={{ background: "rgba(245, 158, 11, 0.08)", border: "1px solid rgba(245, 158, 11, 0.3)", color: "var(--landing-text)" }}
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
            <p>
              {portal.activation
                ? "This checklist is closed for answers. Contact the employer if something needs to change."
                : "The employer has not opened this checklist for answers yet. Check back once they have."}
            </p>
          </div>
        )}

        {total === 0 ? (
          <MessageCard icon={ClipboardList} title="Nothing to complete yet">
            The employer has not added any items to your activation checklist.
          </MessageCard>
        ) : (
          <div className="space-y-4">
            {items.map((item, i) => (
              <ChecklistItemCard
                key={item.id}
                item={item}
                index={i}
                candidateId={portal.candidate.id}
                checklistOpen={open}
                editable={isItemEditable(item, open)}
                onUpdated={handleUpdated}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <DcPage>
      <section
        className="relative min-h-[calc(100vh-80px)] overflow-hidden pt-24 pb-20 md:pt-28"
        style={{ background: "var(--landing-bg)" }}
      >
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(var(--landing-grid-line) 1px, transparent 1px), linear-gradient(90deg, var(--landing-grid-line) 1px, transparent 1px)",
            backgroundSize: "72px 72px",
            maskImage: "radial-gradient(ellipse 70% 50% at 50% 10%, black, transparent)",
            WebkitMaskImage: "radial-gradient(ellipse 70% 50% at 50% 10%, black, transparent)",
          }}
        />
        <div className="container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="mx-auto max-w-3xl"
          >
            {backLink}
            {content}
          </motion.div>
        </div>
      </section>
    </DcPage>
  );
}
